import "./ContentSidebar.scss";

// eslint-disable-next-line no-restricted-imports
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { useIsAssessing } from "../course/assessment/hooks";

import { SECONDARY_OVERLAY_QUERY_PARAM, SIDEBAR_QUERY_PARAM } from "components/constants";
import {
  SIDEBAR_COLLAPSE_EVENT_NAME,
  SIDEBAR_EXPAND_EVENT_NAME,
  SIDEBAR_PANEL_ICONS,
  TAB_EVENT_NAMES,
  TabKeys,
  TabProps,
} from "helpers/sidebar";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { useOnChanged } from "mds/hooks/use-on-changed";
import { useIsXlOrLarger } from "mds/hooks/use-responsive";
import { DoubleChevronLeftIcon, DoubleChevronRightIcon } from "mds/icons";
import { forwardRefComponent } from "mds/utils/components";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectIsRecordingAgent, selectIsSidebarOpen, selectPageById } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { useQueryParamsManager } from "utils/routing";
import { CONTENT_SIDEBAR_TAB_NAME, ViewLevelType } from "utils/urls";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

type ContentSidebarProps = TabProps & {
  context?: ViewLevelType;
  pageId?: string;
  scrollTrackingId?: string;
};

/**
 * This sidebar component is used with various components to show the standard sidebar
 * nav items.  It's a simple tab component styled for icons.  To add a new tab add
 * a new icon to the SIDEBAR_PANEL_ICONS map.  Consumers of this component will
 * pick which tabs to display by passing in the tab content for the given tab.
 * Ex.
 *  <ContentSidebar
 *    ContentViewPanel={<div>Page View Content</div>}
 *    AiAssistantPanel={<div>AI Assistant Content</div>}
 *    ...
 *  />
 *  We pass in the `context` for logging purposes only. This corresponds
 *  to the context for the AI Assistant (if its enabled).
 */
export const ContentSidebar: FC<ContentSidebarProps> = ({
  pageId,
  context,
  scrollTrackingId = "",
  ...tabs
}) => {
  const { deleteQueryParams, getQueryParam, setQueryParams } = useQueryParamsManager();
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  const dispatch = useAppDispatch();
  const canShowBothSidebars = useIsXlOrLarger();

  const isAssessing = useIsAssessing();
  const page = useAppSelector((s) => selectPageById(s, pageId));

  const disabledTabClasses = "cursor-not-allowed opacity-50";

  const maybeCloseSecondarySidebar = useCallback(() => {
    if (!canShowBothSidebars) {
      setQueryParams({
        [SECONDARY_OVERLAY_QUERY_PARAM]: null,
      });
    }
  }, [canShowBothSidebars, setQueryParams]);

  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  useEffect(() => {
    if (isRecordingAgent) {
      dispatch(localStateActions.setIsSidebarOpen(false));
    }
  }, [isRecordingAgent, dispatch]);

  // Close the secondary sidebar if screen size changes to be smaller than xl
  useEffect(() => {
    if (!canShowBothSidebars) {
      maybeCloseSecondarySidebar();
    }
  }, [canShowBothSidebars, maybeCloseSecondarySidebar]);

  const setIsSidebarOpen = useCallback(
    (open: boolean) => {
      maybeCloseSecondarySidebar();
      dispatch(localStateActions.setIsSidebarOpen(open));
    },
    [dispatch, maybeCloseSecondarySidebar],
  );

  const tabNames = useMemo(
    () =>
      Object.keys(tabs)
        // Remove tabs that weren't specified, or tabs that render `null` (due to conditional
        // rendering, like not having the right permissions).
        .filter((tab) => tab && tabs[tab])
        .map((tab) => tab.replace(/Panel$/, "") as CONTENT_SIDEBAR_TAB_NAME)
        .sort((a, b) => TabKeys.indexOf(a) - TabKeys.indexOf(b)),
    [tabs],
  );

  // Note that this may be overridden by the SIDEBAR_QUERY_PARAM
  const defaultSidebarTab: CONTENT_SIDEBAR_TAB_NAME = useMemo(() => {
    if (!page) {
      return tabNames[0];
    }
    if (page.category === "activity_page" && tabs.TopicsPanel) {
      return CONTENT_SIDEBAR_TAB_NAME.Content;
    }
    if (page.category === "assignment" && tabs.AssignmentPanel) {
      return CONTENT_SIDEBAR_TAB_NAME.Assignment;
    }
    if (page.category === "course_resource" && tabs.CourseResourcePanel) {
      return CONTENT_SIDEBAR_TAB_NAME.CourseResource;
    }
    if (page.category === "instructor_workspace" && tabs.InstructorWorkspacePanel) {
      return CONTENT_SIDEBAR_TAB_NAME.InstructorWorkspace;
    }
    if (tabs.PlannerPanel) {
      return CONTENT_SIDEBAR_TAB_NAME.Planner;
    }
    return tabNames[0];
  }, [page, tabNames, tabs]);

  const [selectedTabKey, setSelectedTabKey] = useState<CONTENT_SIDEBAR_TAB_NAME>(defaultSidebarTab);
  const selectedTabIndex = tabNames.indexOf(selectedTabKey);

  useEffect(() => {
    return () => {
      if (scrollTrackingId) {
        dispatch(
          localStateActions.setScrollPosition({
            id: scrollTrackingId,
            position: 0,
          }),
        );
      }
    };
  }, [dispatch, scrollTrackingId]);

  // When available tabs change, then the previously select tab index might not correspond to
  // the same tab name anymore, so we update it here, or to the first available tab if not found.
  useEffect(() => {
    if (!selectedTabKey || !tabNames.includes(selectedTabKey)) {
      setSelectedTabKey(tabNames[0]);
    }
  }, [tabNames, selectedTabKey]);

  useEffect(() => {
    // We use SIDEBAR_QUERY_PARAM solely to determine which sidebar tab
    // should be selected when navigating to a page view.
    if (!!getQueryParam(SIDEBAR_QUERY_PARAM) && isSidebarOpen) {
      const tabKey = getQueryParam(SIDEBAR_QUERY_PARAM) as CONTENT_SIDEBAR_TAB_NAME;

      // wait until after sidebar change possibly effects search params
      setTimeout(() => {
        deleteQueryParams([SIDEBAR_QUERY_PARAM]);
      }, 0);

      if (tabNames.includes(tabKey)) {
        setSelectedTabKey(tabKey);
      }
    }
  }, [isSidebarOpen, tabNames, getQueryParam, deleteQueryParams]);

  useOnChanged(isAssessing, (nowAssessing, wasAssessing) => {
    if (!wasAssessing && nowAssessing) {
      setIsSidebarOpen(false);
    } else if (wasAssessing && !nowAssessing) {
      setIsSidebarOpen(true);
    }
  });

  const onClick = (index: number) => {
    const shouldOpenSidebar = !isSidebarOpen;
    const newSelectedTabKey = tabNames[index];

    const eventName = TAB_EVENT_NAMES[newSelectedTabKey];
    if (eventName) {
      trackEvent(eventName, {
        ...BUTTON_EVENT_CATEGORY,
        context,
        expanded: shouldOpenSidebar,
      });
    }

    if (shouldOpenSidebar) {
      setIsSidebarOpen(true);
    }

    onSelectTab(index);
  };

  const onSelectTab = (index: number) => {
    const newTabKey = tabNames[index];
    if (scrollTrackingId && newTabKey !== selectedTabKey) {
      dispatch(
        localStateActions.setScrollPosition({
          id: scrollTrackingId,
          position: 0,
        }),
      );
    }
    setSelectedTabKey(newTabKey);
  };

  const tabItems = tabNames.map((tab, index) => {
    const IconComponent = forwardRefComponent(SIDEBAR_PANEL_ICONS[tab]);

    return (
      <Tooltip
        content={
          isAssessing
            ? t("main_header.disallowed_while_assessing")
            : t(`content_sidebar.title.${tab}`)
        }
        key={tab}
      >
        <Tab
          aria-label={t(`content_sidebar.title.${tab}`)}
          as="div"
          className={twMerge("tab", isAssessing ? disabledTabClasses : "")}
          disabled={isAssessing}
          onClick={() => onClick(index)}
        >
          <IconComponent />
        </Tab>
      </Tooltip>
    );
  });

  let tabPanels = null;
  if (isSidebarOpen) {
    tabPanels = tabNames.map((tab) => {
      const tabKey = `${tab}Panel` as const;
      const PanelComponent = forwardRefComponent(() => tabs[tabKey], {
        className: "w-full h-full content-sidebar-panel",
      });

      return (
        <TabPanel as={Fragment} key={tabKey}>
          <PanelComponent />
        </TabPanel>
      );
    });
  }

  const openStatus = isSidebarOpen ? "open" : "closed";

  return (
    <ErrorBoundary>
      <TabGroup
        aria-label={t("glossary.sidebar")}
        as="aside"
        className="content-sidebar shrink-0 rounded-l-[inherit]"
        selectedIndex={selectedTabIndex}
      >
        <TabList className="content-sidebar-buttons-bar" data-testid="content-sidebar-tab-list">
          <Tooltip
            content={
              isAssessing
                ? t("main_header.disallowed_while_assessing")
                : t(`content_sidebar.${openStatus}_action`)
            }
          >
            <div
              aria-disabled={isAssessing}
              aria-label={t(`content_sidebar.${openStatus}_action`)}
              className={twMerge("tab", isAssessing ? disabledTabClasses : "")}
              role="button"
              tabIndex={isAssessing ? -1 : 0}
              onClick={
                isAssessing
                  ? undefined
                  : () => {
                      if (!isSidebarOpen) {
                        maybeCloseSecondarySidebar();
                      }
                      trackEvent(
                        isSidebarOpen ? SIDEBAR_COLLAPSE_EVENT_NAME : SIDEBAR_EXPAND_EVENT_NAME,
                        {
                          ...BUTTON_EVENT_CATEGORY,
                          context,
                        },
                      );
                      setIsSidebarOpen(!isSidebarOpen);
                    }
              }
            >
              {isSidebarOpen ? <DoubleChevronLeftIcon /> : <DoubleChevronRightIcon />}
            </div>
          </Tooltip>
          {tabItems}
        </TabList>
        <TabPanels as={Fragment}>{tabPanels}</TabPanels>
      </TabGroup>
    </ErrorBoundary>
  );
};
